import { Icon, cn } from "@sagot/ui";
import { useId, type ReactNode } from "react";
import { isServiceStepKind, type WorkflowChoice, type WorkflowStepPayload } from "../workflow/contract";
import { useWorkflowExpired } from "../workflow/hooks";
import { resolveWorkflowLabels, type WorkflowCardLabels, type WorkflowLabels } from "../workflow/labels";
import { isChosenWorkflowButton, type WorkflowAnswerExtras } from "../workflow/step";
import { selectedChipClass, useChatTheme } from "./chat-theme";
import { ConsentNotice, WorkflowCollectField } from "./workflow-collect-field";
import { WorkflowRating } from "./workflow-rating";
import { WorkflowVerifyCard } from "./workflow-verify-card";

const CHIP = "inline-flex min-h-11 max-w-full items-center gap-1.5 rounded-pill border px-4 py-2 text-left text-sm font-semibold leading-snug disabled:cursor-not-allowed";
const CHIP_OPEN = "border-primary bg-surface text-primary-ink enabled:hover:bg-primary-soft disabled:opacity-50";

export interface WorkflowStepCardProps {
  payload: WorkflowStepPayload;
  /** The visitor picked a button or submitted a value; the host sends it as a chat turn (`answerWorkflowStep`). */
  onAnswer: (card: WorkflowStepPayload, choice: WorkflowChoice, extras?: WorkflowAnswerExtras) => void;
  /** Another turn is in flight: nothing can be answered right now. */
  disabled?: boolean;
  /** The host's single-use bot check for "Send code" / "Resend code" (web app on the platform origin; the widget and the console have none). */
  renderCaptcha?: ((onToken: (token: string | null) => void) => ReactNode) | undefined;
  /** Id of the element holding the step text (the bot bubble): the group is described by it. */
  describedBy?: string;
  labels?: Partial<WorkflowCardLabels>;
}

interface ButtonsProps {
  card: WorkflowStepPayload;
  labels: WorkflowLabels;
  describedBy: string | undefined;
  settled: boolean;
  disabled: boolean;
  onAnswer: WorkflowStepCardProps["onAnswer"];
}

function ReplyButtons({ card, labels, describedBy, settled, disabled, onAnswer }: ButtonsProps) {
  const { themed } = useChatTheme();
  return (
    <div role="group" aria-label={labels.choose} aria-describedby={describedBy} className="mt-2 flex flex-wrap gap-2">
      {card.buttons.map((button) => {
        const chosen = settled && isChosenWorkflowButton(card, button);
        return (
          <button
            key={button.id}
            type="button"
            disabled={settled || disabled}
            aria-pressed={settled ? chosen : undefined}
            onClick={() => {
              onAnswer(card, { button_id: button.id });
            }}
            className={cn(CHIP, chosen ? cn(selectedChipClass(themed), "disabled:opacity-100") : CHIP_OPEN, settled && !chosen && "disabled:opacity-50")}
          >
            {chosen ? <Icon name="check" className="size-4 shrink-0" /> : null}
            <span className="min-w-0 break-words">{button.label}</span>
            {chosen ? <span className="sr-only">{`(${labels.chosen})`}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function Closed({ text }: { text: string }) {
  return (
    <p className="mt-2 flex items-center gap-1.5 text-xs text-fg-muted">
      <Icon name="clock" className="size-4 shrink-0 text-fg-subtle" />
      <span className="min-w-0 break-words">{text}</span>
    </p>
  );
}

/**
 * A question the visitor answers in the composer (`collect_reply`): the card has no field of its
 * own, only the privacy notice when the answer is saved to personal data. The composer switches to
 * its reply mode while the card is open (see `workflowComposerState`).
 */
function ReplyNote({ card, labels }: { card: WorkflowStepPayload; labels: WorkflowLabels }) {
  const id = useId();
  if (!card.consent_text) return null;
  return <ConsentNotice id={`${id}-consent`} text={card.consent_text} url={card.privacy_url} linkLabel={labels.privacyLink} />;
}

function AnsweredValue({ card, labels }: { card: WorkflowStepPayload; labels: WorkflowLabels }) {
  const value = card.answer?.label ?? card.answer?.value;
  if (!value) return null;
  return (
    <p className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-fg-muted">
      <Icon name="check-circle" className="size-4 shrink-0 text-success" />
      <span className="min-w-0 break-words">{labels.answeredWith(value)}</span>
    </p>
  );
}

/**
 * A workflow question under the bot's text (`workflow_step` action): reply buttons, one field to
 * fill, a rating or the code of a verified e-mail. Answered cards stay as a record (the picked button marked with a check, the
 * entered value or the rating); an expired one closes with a hint to type instead.
 */
export function WorkflowStepCard({ payload, onAnswer, disabled = false, describedBy, labels: overrides, renderCaptcha }: WorkflowStepCardProps) {
  const labels = resolveWorkflowLabels(overrides);
  const expired = useWorkflowExpired(payload);
  const answered = payload.state === "answered";
  if (payload.kind === "reply_buttons") {
    if (expired && !answered) return <Closed text={labels.expired} />;
    return <ReplyButtons card={payload} labels={labels} describedBy={describedBy} settled={answered} disabled={disabled} onAnswer={onAnswer} />;
  }
  if (payload.kind === "rating") {
    if (expired && !answered) return <Closed text={labels.expired} />;
    return <WorkflowRating card={payload} labels={labels} describedBy={describedBy} settled={answered} disabled={disabled} onAnswer={onAnswer} />;
  }
  if (payload.kind === "verify_email") {
    if (answered) return null;
    if (expired) return <Closed text={labels.expired} />;
    return <WorkflowVerifyCard card={payload} labels={labels} disabled={disabled} describedBy={describedBy} onAnswer={onAnswer} renderCaptcha={renderCaptcha} />;
  }
  if (payload.kind === "collect_reply") {
    if (answered) return null;
    return expired ? <Closed text={labels.expired} /> : <ReplyNote card={payload} labels={labels} />;
  }
  if (isServiceStepKind(payload.kind)) return null; // the ticket cards are drawn by the widget's service chunk only
  if (answered) return <AnsweredValue card={payload} labels={labels} />;
  if (expired || !payload.field) return <Closed text={labels.expired} />;
  return <WorkflowCollectField card={payload} field={payload.field} labels={labels} disabled={disabled} describedBy={describedBy} onAnswer={onAnswer} />;
}
